import ReactDOM from "react-dom/client";
import React, { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import Select from "react-select";
import NotificationEventTypes from "../../constants/notificationEventsEnum";

function WithdrawalInterviewModal({
  onClose,
  withdrawalId,
  refreshOnSubmitSuccess,
  studentFirstRegistrationId,
}) {
  const token = localStorage.getItem("token");
  const centerId = localStorage.getItem("centerId");

  const [isEditMode, setIsEditMode] = useState(false);
  const [isReschedule, setIsReschedule] = useState(false);
  const [createdBy, setCreatedBy] = useState("");
  const [slotsData, setSlotsData] = useState([]);
  const [interviewScheduleDate, setInterviewScheduleDate] = useState("");
  const [teachersData, setTeachersData] = useState([]);

  const toggleReschedule = () => {
    setIsReschedule((prevState) => !prevState);
  };

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    setValue,
    trigger,
    reset,
  } = useForm({
    defaultValues: {
      id: withdrawalId,
      employeeIds: [],
      employeeEmails: "",
      interviewScheduleDate: "",
      timeSlotId: null,
      interviewScheduledRemarks: "",
      interviewReScheduleReason: "",
      sectionHeadName: "",
      createdBy: localStorage.getItem("userId"),
      updatedBy: localStorage.getItem("userId"),
    },
  });

  const getUsersByCenterIdAndRoleId = async () => {
    try {
      const response = await get(
        `UserAuthentication/GetUsersByCenterIdAndRoleId?centerId=${centerId}&roleId=${7}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setTeachersData(response || []);
    } catch (error) {
      console.error("Error fetching teachers:", error);
    }
  };

  const fetchScheduleDetails = async () => {
    try {
      const response = await get(
        `Withdrawal/GetWithdrawalById?withdrawalId=${withdrawalId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response?.isSuccess && response?.withdrawal?.id) {
        const scheduleData = response.withdrawal;
        setCreatedBy(scheduleData.createdBy);
        setInterviewScheduleDate(scheduleData.interviewScheduleDate);
        setIsEditMode(!!scheduleData.interviewScheduleDate);

        let timeSlotLabel = scheduleData.timeSlotId?.toString() || "";
        if (scheduleData.interviewScheduleDate && scheduleData.timeSlotId) {
          try {
            const slotResponse = await get(
              `CommonSettings/GetTimeSlotByDateAndSlotTypeAsync?date=${scheduleData.interviewScheduleDate}&slotType=Interview&centerId=${centerId}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            const slot = slotResponse?.timeSlot?.find(
              (s) => s.id === scheduleData.timeSlotId
            );
            if (slot) {
              timeSlotLabel = `${slot.fromTime} - ${slot.toTime}`;
              setSlotsData(slotResponse?.timeSlot || []);
            }
          } catch (error) {
            console.error("Error fetching time slot details:", error);
          }
        }

        const selectedTeachers = scheduleData.employeeIds
          ? scheduleData.employeeIds.split(",").map((id) => {
              const teacherInfo = teachersData.find(
                (td) => td.id.toString() === id.trim()
              );
              return {
                value: id.trim(),
                label: teacherInfo
                  ? `${teacherInfo.firstName} ${teacherInfo.lastName}`
                  : `Teacher ${id.trim()}`,
              };
            })
          : [];

        reset({
          id: scheduleData.id,
          employeeIds: selectedTeachers,
          employeeEmails: scheduleData.employeeEmails || "",
          interviewScheduleDate: scheduleData.interviewScheduleDate
            ? scheduleData.interviewScheduleDate.split("T")[0]
            : "",
          timeSlotId: scheduleData.timeSlotId
            ? {
                value: scheduleData.timeSlotId,
                label: timeSlotLabel,
              }
            : null,
          interviewScheduledRemarks:
            scheduleData.interviewScheduledRemarks || "",
          interviewReScheduleReason:
            scheduleData.interviewReScheduleReason || "",
          sectionHeadName: scheduleData.sectionHeadName,
          createdBy: scheduleData.createdBy || localStorage.getItem("userId"),
          updatedBy: localStorage.getItem("userId"),
        });
      } else {
        setIsEditMode(false);
        reset({
          id: withdrawalId,
          employeeIds: [],
          employeeEmails: "",
          interviewScheduleDate: "",
          timeSlotId: null,
          interviewScheduledRemarks: "",
          interviewReScheduleReason: "",
          sectionHeadName: "",
          createdBy: localStorage.getItem("userId"),
          updatedBy: localStorage.getItem("userId"),
        });
      }
    } catch (error) {
      console.error("Error fetching schedule data", error);
    }
  };

  useEffect(() => {
    if (withdrawalId && teachersData.length > 0) {
      fetchScheduleDetails();
    }
  }, [withdrawalId, teachersData]);

  useEffect(() => {
    getUsersByCenterIdAndRoleId();
  }, []);

  useEffect(() => {
    if (interviewScheduleDate) {
      fetchInterviewSlots();
    }
  }, [interviewScheduleDate]);

  const fetchInterviewSlots = async () => {
    try {
      const response = await get(
        `CommonSettings/GetTimeSlotByDateAndSlotTypeAsync?date=${interviewScheduleDate}&slotType=Interview&centerId=${centerId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSlotsData(response?.timeSlot || []);
    } catch (error) {
      console.error("Error fetching time slots:", error);
    }
  };

  const saveExitInterview = async (data) => {
    const selectedTeachers = data.employeeIds || [];

    const formData = {
      id: withdrawalId,
      studentFirstRegistrationId: studentFirstRegistrationId,
      employeeIds: selectedTeachers.map((teacher) => teacher.value).join(","),
      employeeEmails: data.employeeEmails || "",
      interviewScheduleDate: data.interviewScheduleDate
        ? new Date(data.interviewScheduleDate).toISOString()
        : null,
      timeSlotId: data.timeSlotId?.value || 0,
      interviewScheduledRemarks: data.interviewScheduledRemarks || "",
      interviewReScheduleReason: isReschedule
        ? data.interviewReScheduleReason
        : "",
      createdBy: isEditMode
        ? createdBy || localStorage.getItem("userId")
        : localStorage.getItem("userId"),
      updatedBy: localStorage.getItem("userId"),
      FormId: 59,
      NotificationEventTypeIds: NotificationEventTypes.REMINDER.toString(),
    };

    try {
      const response = await post(
        "Withdrawal/UpdateWithdrawalInterviewSchedule",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.isSuccess) {
        toast.success("Exit interview saved successfully!");
        onClose();
        refreshOnSubmitSuccess();
      } else {
        toast.error("Failed to save exit interview.");
      }
    } catch (error) {
      console.error("Error saving exit interview:", error);
      toast.error("An error occurred while saving the exit interview.");
    }
  };

  return (
    <>
      <div
        className="modal fade show"
        tabIndex="-1"
        style={{ display: "block" }}
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="mb-0">Schedule Exit Interview</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              <form
                className="row g-3 new-form-style"
                onSubmit={handleSubmit(saveExitInterview)}
              >
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Interview Date<span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    type="date"
                    {...register("interviewScheduleDate", {
                      required: "This field is required.",
                    })}
                    className="form-control"
                    onChange={(e) => {
                      setInterviewScheduleDate(e.target.value);
                      setValue("timeSlotId", null);
                    }}
                    disabled={isEditMode && !isReschedule}
                  />
                  {errors.interviewScheduleDate && (
                    <small className="text-danger">
                      {errors.interviewScheduleDate.message}
                    </small>
                  )}
                </div>
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Time Slot<span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="timeSlotId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={slotsData?.map((slot) => ({
                          value: slot.id,
                          label: `${slot.fromTime} - ${slot.toTime}`,
                        }))}
                        onChange={(selectedOption, actionMeta) => {
                          if (actionMeta.action === "clear") {
                            setValue("timeSlotId", null);
                          } else {
                            setValue("timeSlotId", selectedOption);
                          }
                          trigger("timeSlotId");
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                        isClearable
                        isDisabled={isEditMode && !isReschedule}
                      />
                    )}
                  />
                  {errors.timeSlotId && (
                    <small className="text-danger">
                      {errors.timeSlotId.message}
                    </small>
                  )}
                </div>
                <div className="col-md-12">
                  <label className="form-label form-label-new">
                    Remarks<span className="text-danger fs-9">*</span>
                  </label>
                  <textarea
                    {...register("interviewScheduledRemarks", {
                      required: "This field is required.",
                    })}
                    className="form-control"
                    disabled={isEditMode && !isReschedule}
                  ></textarea>
                  {errors.interviewScheduledRemarks && (
                    <small className="text-danger">
                      {errors.interviewScheduledRemarks.message}
                    </small>
                  )}
                </div>
                {isEditMode && isReschedule && (
                  <div className="col-md-12">
                    <label className="form-label form-label-new">
                      Reschedule Reason
                      <span className="text-danger fs-9">*</span>
                    </label>
                    <textarea
                      {...register("interviewReScheduleReason", {
                        required: "This field is required.",
                      })}
                      className="form-control"
                    ></textarea>
                    {errors.interviewReScheduleReason && (
                      <small className="text-danger">
                        {errors.interviewReScheduleReason.message}
                      </small>
                    )}
                  </div>
                )}
                <div className="d-flex gap-2 justify-content-end">
                  {isEditMode && (
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm"
                      onClick={toggleReschedule}
                    >
                      {isReschedule ? "Cancel" : "Reschedule"}
                    </button>
                  )}
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span
                        className="spinner-border spinner-custom-size me-1"
                        aria-hidden="true"
                      ></span>
                    ) : null}
                    <span role="status">Save</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
}

export default WithdrawalInterviewModal;